import { useEffect, useState, type ReactNode } from "react"
import { useAttachmentSrc } from "./hooks/useAttachmentSrc"
import { useCurrentAvatar } from "./hooks/current-avatars"

interface AvatarPhotoProps {
  /** The raw profile/group picture URL, bearer-authed or public, or absent. */
  src?: string | null
  alt: string
  className?: string
  /** Whatever this spot showed before a photo existed: initials, an emoji,
   *  an icon. Shown with no src, while a protected src is still resolving,
   *  and after the image fails to load. */
  fallback: ReactNode
  /**
   * The person's contactPresenceId, when this avatar belongs to a PERSON
   * (never a group). `src` is often a copy taken when the conversation/group
   * was created (`conversation_participants.avatar_url`), which 404s the
   * moment that person changes their picture, because the backend deletes
   * the old file. With a `contactId`, the live picture from the directory
   * wins over that stale copy.
   */
  contactId?: string | null
}

/**
 * A contact/group picture spot that never shows the browser's broken-image
 * icon plus alt text.
 *
 * `useAttachmentSrc` resolves a bearer-authed attachment URL (or passes a
 * public one straight through) and already falls back to `undefined` on a
 * failed fetch. What it cannot catch is an URL that resolves fine but then
 * 404s, gets deleted, or points at the wrong host once the browser actually
 * loads it: that only shows up as the `<img>`'s own `onError`. Every avatar
 * spot used to render a bare `<img src={imageUrl}>` with neither case
 * handled, which is what the broken icon was.
 */
export function AvatarPhoto({ src, alt, className, fallback, contactId }: AvatarPhotoProps) {
  const currentAvatar = useCurrentAvatar(contactId)
  // `undefined` covers "don't know yet", "no such contact" and "server has no
  // /contacts/lookup route" alike: fall back to whatever `src` this spot was
  // already given rather than show nothing while that resolves.
  const effectiveSrc = currentAvatar === undefined ? src : currentAvatar
  const resolved = useAttachmentSrc(effectiveSrc)
  const [failed, setFailed] = useState(false)

  // A new src deserves its own chance, otherwise a contact whose previous
  // photo failed would be stuck on the fallback even after they set a new,
  // working one (or the live lookup above swapped in a different URL).
  useEffect(() => {
    setFailed(false)
  }, [effectiveSrc])

  if (!resolved || failed) return <>{fallback}</>
  return <img src={resolved} alt={alt} className={className} onError={() => setFailed(true)} />
}
